import React, { useState, useRef, useEffect } from 'react';
import { View, Text, Animated } from 'react-native';
import i18n from 'i18n-js';

import { scale } from '@components/frame';
import { isWeb, dateToSeconds, convertTimeLetter, isPhone, Alert, localTime } from '@helpers';

import TouchableElement from '@components/common/touchableElement';
import EditableLine from '@components/common/editableLine';
import Line from '@components/common/line';
import WheelTimePicker from '@components/common/WheelTimePicker';
import LineButton from '@components/common/lineButton';

import { SmallLoading } from '@components/loading';

import { font } from '@styles/fonts';
import { modalFieldBg, TEST_IDS } from '@v2/pages/common/constants/base.constants';

export default ({
  C,
  setSelectToolCloseModal,
  params,
  hide,
  setMode,
  MODES,
  clampingTool,
  editClampingTools,
  createClampingTools,
  getClampingTools,
  newTool,
  tool,
  removeClampingTool,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [deletingClamping, setDeletingClamping] = useState(false);

  const duration = convertTimeLetter(clampingTool.machiningDuration);
  const [data, setData] = useState({
    machiningDuration: { h: Number(duration.h), m: Number(duration.m), s: Number(duration.s) },
    toolNumber: String(clampingTool.toolNumber || ''),
    memo: String(clampingTool.memo || ''),
  });

  useEffect(() => {
    setSelectToolCloseModal(false);
  }, []);
  const submitDisabled = !params?.ncFileId || !data.toolNumber;

  const submit = async () => {
    setIsLoading(true);
    if (submitDisabled) {
      return;
    }
    const ncFileId = params?.ncFileId || clampingTool.ncFileId;
    if (newTool) {
      await createClampingTools({
        toolNumber: Number(parseInt(data.toolNumber)),
        machiningDuration: dateToSeconds(data.machiningDuration),
        memo: data.memo,
        toolId: tool?.toolId,
        ncFileId: Number(params?.ncFileId),
      });
    } else {
      await editClampingTools({
        clampingToolId: clampingTool.clampingToolId,
        toolNumber: Number(data.toolNumber),
        machiningDuration: dateToSeconds(data.machiningDuration),
        memo: data.memo,
        toolId: tool.toolId,
        ncFileId: Number(clampingTool.ncFileId),
      });
    }
    setIsLoading(false);
    await getClampingTools({ ncFileId });
    hide();
  };

  const updateData = (key, value) => {
    const newData = Object.assign({}, data);
    newData[key] = value;
    setData(newData);
  };

  const mainInfo = [
    {
      name: 'Tool Number',
      key: 'toolNumber',
      placeholder: 'Tool Number',
      keyboardType: 'number-pad',
      small: isPhone,
    },
    {
      name: 'Memo',
      key: 'memo',
      placeholder: 'Memo',
      small: isPhone,
    },
    {
      name: 'Machining Time',
      Component: WheelTimePicker,
      noArrow: true,
      key: 'machiningDuration',
      isTime: true,
      small: false,
    },
  ];

  const toolLine = [
    {
      name: 'Tool',
      key: 'tool',
      value: tool?.toolName,
      onClick: () => setMode(newTool ? MODES.SELECT_TOOL : MODES.TOOL_DETAILS),
    },
  ];

  const [pressStyle, setPressStyle] = useState({
    machiningDuration: true,
  });

  const baseHeight = 0;
  const animation = {
    machiningDuration: useRef(new Animated.Value(baseHeight)).current,
  };

  const openKey = (key) => {
    if (isWeb) {
      return;
    }

    Object.keys(animation).map((k) => {
      if (k !== key) {
        Animated.timing(animation[k], {
          toValue: baseHeight,
          duration: 500,
          useNativeDriver: false,
        }).start();
      }
    });
    const big = scale(200);

    const pressStyle = Object.assign({}, data);
    if (animation[key] && animation[key]._value == big) {
      Animated.timing(animation[key], {
        toValue: baseHeight,
        duration: 500,
        useNativeDriver: false,
      }).start();
      pressStyle[key] = true;
    } else {
      if (animation[key]) {
        Animated.timing(animation[key], {
          toValue: big,
          duration: 500,
          useNativeDriver: false,
        }).start();
        pressStyle[key] = false;
      }
    }
    setPressStyle(pressStyle);
  };

  const triggerDelete = async (FN) => {
    Alert({
      title: i18n.t('Are you sure?'),
      actions: [
        {
          text: i18n.t('No'),
          onPress: () => {},
          style: 'primary',
        },
        {
          text: i18n.t('Yes'),
          onPress: async () => {
            await FN();
          },
        },
      ],
    });
  };

  const deleteClampingTool = async () => {
    setDeletingClamping(true);
    const result = await removeClampingTool({
      clampingToolId: clampingTool.clampingToolId,
    });
    setDeletingClamping(false);
    if (result) {
      hide();
    }
  };

  return (
    <View testID={TEST_IDS.clampingTool.detailsPopup} style={{ paddingBottom: scale(15) }}>
      <View>
        <View
          style={{
            width: '100%',
            zIndex: 1000,
            height: scale(58),
          }}
        >
          <View style={{ position: 'absolute', zIndex: 1000, left: 0 }}>
            <TouchableElement
              onPress={() => {
                hide();
              }}
            >
              {({ boxStyle, pressHoverColor }) => (
                <View style={[boxStyle, { padding: scale(15) }]}>
                  <Text style={[{ color: C(pressHoverColor('System/Light/Blue')) }, font.Body]}>{i18n.t('Cancel')}</Text>
                </View>
              )}
            </TouchableElement>
          </View>
          <View style={{ position: 'absolute', zIndex: 1000, right: 0 }}>
            <TouchableElement
              onPress={() => {
                submitDisabled ? null : submit();
              }}
            >
              {({ boxStyle, pressHoverColor }) => (
                <View style={[boxStyle, { padding: scale(15) }]}>
                  {isLoading ? (
                    <SmallLoading />
                  ) : (
                    <Text style={[{ color: C(pressHoverColor(submitDisabled ? 'Label/Light/Quinary' : 'Link/Light/Primary')) }, font.Headline]}>
                      {i18n.t('Done')}
                    </Text>
                  )}
                </View>
              )}
            </TouchableElement>
          </View>
        </View>
        <View style={{ width: '100%', position: 'absolute', zIndex: 100, top: scale(15), alignItems: 'center' }}>
          <Text
            numberOfLines={1}
            ellipsizeMode="middle"
            style={[{ color: C('Label/Light/Primary'), width: '60%', textAlign: 'center' }, font.Headline]}
          >
            {newTool ? (
              <Text style={[{ color: C('Label/Light/Primary'), width: '100%', textAlign: 'center' }, font.Headline]}>
                {i18n.t('Add Tool to NC File')}
              </Text>
            ) : (
              <>
                <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>T{clampingTool.toolNumber}</Text>
                <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}> {tool?.toolName}</Text>
              </>
            )}
          </Text>
          <View style={{ width: '100%' }}>
            <Text style={[{ color: C('Label/Light/Tertiary'), width: '100%', textAlign: 'center' }, font.Footnote]}>{params?.ncFile}</Text>
          </View>
        </View>
      </View>
      <View style={{ marginTop: scale(25), paddingHorizontal: scale(15) }}>
        {toolLine.map((item, index) => (
          <Line
            {...{
              item,
              bg: modalFieldBg,
              index,
              array: toolLine,
              C,
              bottomBorderBox: false,
              topBorderBox: false,
              topBorder: index !== 0,
              roundTop: index === 0,
              roundBottom: index === toolLine.length - 1,
            }}
            key={item.key}
          />
        ))}
      </View>
      <View style={{ marginTop: scale(30), paddingHorizontal: scale(15) }}>
        {mainInfo.map((item, index) => (
          <EditableLine
            {...{
              item: {
                value: data[item.key],
                onChange: (v) => updateData(item.key, v),
                onClick: () => openKey(item.key),
                animatedHeight: animation[item.key],
                justifyContent: 'flex-end',
                paddingRight: scale(20),
                isOffset: false,
                ...item,
              },
              bg: modalFieldBg,
              pressStyle: pressStyle[item.key],
              index,
              array: mainInfo,
              C,
              bottomBorderBox: false,
              topBorderBox: false,
              topBorder: index !== 0,
              roundTop: index === 0,
              roundBottom: index === mainInfo.length - 1,
            }}
            key={item.key}
          />
        ))}
      </View>
      {!newTool && (
        <View style={{ marginHorizontal: scale(15) }}>
          <View style={{ marginTop: scale(20) }}>
            <LineButton
              {...{
                C,
                onPress: () => {
                  if (deletingClamping) {
                    return;
                  }
                  triggerDelete(deleteClampingTool);
                },
                bg: modalFieldBg,
                loading: deletingClamping,
                text: i18n.t('Remove'),
                color: 'Label / Light / Error',
              }}
            />
          </View>
          <View style={{ marginTop: scale(6), marginHorizontal: scale(15) }}>
            <Text
              style={[
                {
                  color: C('Label/Light/Tertiary'),
                },
                font['Caption 1'],
              ]}
            >
              {i18n.t('Remove')} {tool?.toolName} {i18n.t('from')} {params.ncFile}
            </Text>
          </View>
          <View style={{ paddingHorizontal: scale(15), marginTop: scale(26) }}>
            <Text
              style={[
                {
                  color: C('Label/Light/Tertiary'),
                },
                font['Caption 1'],
              ]}
            >
              {i18n.t('Last modified on')} {clampingTool.lastModifiedOn ? localTime(clampingTool.lastModifiedOn).format('DD-MM-YYYY') : ''}{' '}
              {i18n.t('by')} {clampingTool.lastModifiedByUser}
            </Text>
            <Text
              style={[
                {
                  color: C('Label/Light/Tertiary'),
                },
                font['Caption 1'],
              ]}
            >
              {i18n.t('Created on')} {clampingTool.createdOn ? localTime(clampingTool.createdOn).format('DD-MM-YYYY') : ''} {i18n.t('by')}{' '}
              {clampingTool.createdByUser}
            </Text>
          </View>
        </View>
      )}
    </View>
  );
};
